<template>
    <div>
      <Navbar />
  
      <main>
  
        <!-- ── Page hero ── -->
        <section class="page-hero">
          <div class="page-hero-overlay"></div>
          <div class="page-container">
            <div class="page-hero-content">
              <div class="hero-badge">
                <font-awesome-icon icon="search" class="me-1" /> Search Results
              </div>
              <h1 class="page-hero-title">
                Results for <span class="hero-query">"{{ title }}"</span>
              </h1>
              <nav class="breadcrumb-nav" aria-label="Breadcrumb">
                <router-link to="/home" class="breadcrumb-link">Home</router-link>
                <span class="breadcrumb-sep">/</span>
                <router-link to="/blog" class="breadcrumb-link">Blog</router-link>
                <span class="breadcrumb-sep">/</span>
                <span class="breadcrumb-current">Search</span>
              </nav>
            </div>
          </div>
        </section>
  
        <!-- ── Results ── -->
        <section class="section-pad">
          <div class="page-container">
  
            <!-- Loading -->
            <div v-if="loading" class="loading-state">
              <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
              <p class="loading-text">Searching posts...</p>
            </div>
  
            <!-- Empty -->
            <div v-else-if="!details.length" class="empty-state">
              <div class="empty-icon-wrap">
                <font-awesome-icon icon="search" class="empty-icon" />
              </div>
              <h3 class="empty-title">No results found</h3>
              <p class="empty-sub">
                We couldn't find any posts matching <strong>"{{ title }}"</strong>. Try a different keyword.
              </p>
              <div class="empty-search-wrap">
                <form @submit.prevent="doSearch">
                  <div class="inline-search">
                    <input
                      v-model="searchInput"
                      type="text"
                      class="inline-search-input"
                      placeholder="Try another search..."
                    />
                    <button type="submit" class="inline-search-btn">
                      <font-awesome-icon icon="search" />
                    </button>
                  </div>
                </form>
              </div>
              <router-link to="/blog" class="btn-outline-md">
                <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Blog
              </router-link>
            </div>
  
            <template v-else>
  
              <!-- Count + search again -->
              <div class="list-header">
                <span class="list-count">
                  <strong>{{ pagination.total }}</strong> result{{ pagination.total !== 1 ? 's' : '' }}
                  for <strong>"{{ title }}"</strong>
                </span>
                <form @submit.prevent="doSearch" class="inline-search inline-search--sm">
                  <input
                    v-model="searchInput"
                    type="text"
                    class="inline-search-input"
                    placeholder="Search again..."
                  />
                  <button type="submit" class="inline-search-btn">
                    <font-awesome-icon icon="search" />
                  </button>
                </form>
              </div>
  
              <!-- Posts -->
              <div class="posts-grid">
                <article v-for="blog in details" :key="blog.id" class="post-card">
  
                  <!-- Thumbnail -->
                  <router-link :to="'/blog/' + blog.slug" class="post-thumb-wrap">
                    <img
                      v-if="blog.image"
                      :src="url.hostName + 'images/blog/' + blog.image"
                      :alt="blog.title"
                      class="post-thumb"
                    />
                    <div v-else class="post-thumb-placeholder">
                      <font-awesome-icon icon="newspaper" />
                    </div>
                    <span v-if="blog.category" class="post-category-badge">
                      {{ blog.category.name }}
                    </span>
                  </router-link>
  
                  <!-- Body -->
                  <div class="post-body">
                    <h2 class="post-title">
                      <router-link :to="'/blog/' + blog.slug" class="post-title-link">
                        {{ blog.title }}
                      </router-link>
                    </h2>
  
                    <p class="post-excerpt">{{ excerpt(blog.description) }}</p>
  
                    <div class="post-footer">
                      <div class="post-meta">
                        <span class="meta-item">
                          <font-awesome-icon icon="calendar-alt" class="meta-icon" />
                          {{ formatDate(blog.created_at) }}
                        </span>
                        <span class="meta-item">
                          <font-awesome-icon icon="comments" class="meta-icon" />
                          {{ blog.comments?.length ?? 0 }}
                        </span>
                      </div>
                      <router-link :to="'/blog/' + blog.slug" class="read-more">
                        Read More <font-awesome-icon icon="chevron-right" class="ms-1" />
                      </router-link>
                    </div>
                  </div>
  
                </article>
              </div>
  
              <!-- Pagination -->
              <div class="pagination-wrap">
                <pagination
                  :pagination="pagination"
                  @prev="pageData(pagination.prevPageUrl)"
                  @next="pageData(pagination.nextPageUrl)"
                />
              </div>
  
            </template>
  
          </div>
        </section>
  
      </main>
  
      <Footer />
    </div>
  </template>
  
  <script>
  import axios   from 'axios';
  import moment  from 'moment-timezone';
  import Navbar  from '../components/Navbar';
  import Footer  from '../components/footer';
  import hostName from '../main';
  
  export default {
    name: 'BlogSearch',
  
    components: { Navbar, Footer },
  
    data() {
      return {
        details:     [],
        loading:     true,
        url:         hostName,
        title:       this.$route.query.search ?? '',
        searchInput: this.$route.query.search ?? '',
  
        pagination: {
          lastPage: '', currentPage: '', total: '',
          lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
          from: '', to: '',
        },
      };
    },
  
    methods: {
      formatDate(date) {
        return moment(date).format('MMM DD, YYYY');
      },
  
      excerpt(html, len = 120) {
        const text = new DOMParser()
          .parseFromString(html ?? '', 'text/html')
          .body.textContent ?? '';
        return text.length > len ? text.substring(0, len).trimEnd() + '…' : text;
      },
  
      doSearch() {
        if (!this.searchInput.trim()) return;
        this.$router.push('/search/blog?search=' + encodeURIComponent(this.searchInput.trim()));
        this.title = this.searchInput.trim();
        this.pageData();
      },
  
      async pageData(url) {
        this.loading = true;
        try {
          const { data } = await axios.post(url ?? '/admin/blog-search/', {
            search: this.title,
          });
          this.details = data.data.data;
          this.configPagination(data.data);
        } catch (err) {
            console.log(err?.response?.data?.message);
        }
        finally { this.loading = false; }
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  
    mounted() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.pageData();
    },
  };
  </script>
  
  <style scoped>
  /* ── Shared ──────────────────────────────────────────────────────────── */
  .page-container { max-width:1160px; margin:0 auto; padding:0 24px; }
  .section-pad    { padding:64px 0 80px; }
  .me-1           { margin-right:5px; }
  .ms-1           { margin-left:5px; }
  
  /* ── Page hero ───────────────────────────────────────────────────────── */
  .page-hero {
    position: relative;
    background: linear-gradient(135deg, #051B35 0%, #004AAD 100%);
    padding: 80px 0 56px;
    overflow: hidden;
  }
  .page-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
    opacity: 0.05;
  }
  .page-hero-overlay { position:absolute; inset:0; background:linear-gradient(135deg, rgba(5,27,53,.95) 0%, rgba(0,74,173,.78) 100%); }
  .page-hero-content { position:relative; z-index:1; text-align:center; }
  
  .hero-badge {
    display: inline-flex; align-items:center; gap:6px;
    background: rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.18);
    color: rgba(255,255,255,.9); border-radius:20px;
    padding: 4px 14px; font-size:12px; font-weight:700;
    text-transform:uppercase; letter-spacing:.07em; margin-bottom:16px;
  }
  .page-hero-title { font-size:clamp(24px,4.5vw,40px); font-weight:800; color:#fff; margin:0 0 18px; }
  .hero-query      { color:#7eb8ff; font-style:italic; }
  
  .breadcrumb-nav     { display:inline-flex; align-items:center; gap:8px; font-size:14px; }
  .breadcrumb-link    { color:rgba(255,255,255,.65); text-decoration:none; transition:color .15s; }
  .breadcrumb-link:hover { color:#fff; }
  .breadcrumb-sep     { color:rgba(255,255,255,.3); }
  .breadcrumb-current { color:rgba(255,255,255,.45); }
  
  /* ── Loading ─────────────────────────────────────────────────────────── */
  .loading-state { display:flex; flex-direction:column; align-items:center; gap:16px; padding:80px 0; }
  .loading-text  { font-size:14px; color:#7a90b0; margin:0; }
  
  /* ── Empty ───────────────────────────────────────────────────────────── */
  .empty-state       { display:flex; flex-direction:column; align-items:center; text-align:center; padding:64px 24px; gap:16px; }
  .empty-icon-wrap   { width:72px; height:72px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:28px; }
  .empty-icon        { font-size:28px; }
  .empty-title       { font-size:20px; font-weight:700; color:#1a2b45; margin:0; }
  .empty-sub         { font-size:14px; color:#7a90b0; margin:0; max-width:380px; line-height:1.6; }
  .empty-search-wrap { width:100%; max-width:380px; }
  
  /* ── Inline search ───────────────────────────────────────────────────── */
  .inline-search { display:flex; gap:0; border:1px solid #dde5f0; border-radius:9px; overflow:hidden; background:#fff; }
  .inline-search--sm { width:260px; }
  .inline-search-input { flex:1; padding:10px 14px; border:none; outline:none; font-size:13.5px; color:#1a2b45; font-family:inherit; }
  .inline-search-btn { padding:10px 16px; background:#004AAD; border:none; color:#fff; font-size:13px; cursor:pointer; transition:background .15s; flex-shrink:0; }
  .inline-search-btn:hover { background:#003b8e; }
  
  /* ── List header ─────────────────────────────────────────────────────── */
  .list-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:32px; flex-wrap:wrap; gap:14px; }
  .list-count  { font-size:14px; color:#7a90b0; }
  .list-count strong { color:#1a2b45; }
  
  /* ── Posts grid ──────────────────────────────────────────────────────── */
  .posts-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:28px; }
  
  /* ── Post card ───────────────────────────────────────────────────────── */
  .post-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; display:flex; flex-direction:column; transition:box-shadow .2s, transform .2s; }
  .post-card:hover { box-shadow:0 12px 32px rgba(0,0,0,.1); transform:translateY(-4px); }
  
  .post-thumb-wrap { position:relative; display:block; text-decoration:none; overflow:hidden; aspect-ratio:16/9; background:#f4f7fb; }
  .post-thumb { width:100%; height:100%; object-fit:cover; transition:transform .3s ease; display:block; }
  .post-card:hover .post-thumb { transform:scale(1.04); }
  .post-thumb-placeholder { width:100%; height:100%; display:flex; align-items:center; justify-content:center; font-size:32px; color:#c0cce0; }
  .post-category-badge { position:absolute; top:12px; left:12px; background:#004AAD; color:#fff; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; border-radius:20px; padding:3px 10px; }
  
  .post-body    { padding:20px; display:flex; flex-direction:column; gap:10px; flex:1; }
  .post-title   { font-size:15.5px; font-weight:700; color:#1a2b45; margin:0; line-height:1.4; }
  .post-title-link { color:inherit; text-decoration:none; transition:color .15s; }
  .post-title-link:hover { color:#004AAD; }
  .post-excerpt { font-size:13.5px; color:#6b7a99; line-height:1.6; margin:0; flex:1; }
  
  .post-footer { display:flex; align-items:center; justify-content:space-between; padding-top:14px; border-top:1px solid #f0f4f8; flex-wrap:wrap; gap:8px; }
  .post-meta   { display:flex; align-items:center; gap:14px; }
  .meta-item   { display:inline-flex; align-items:center; gap:5px; font-size:12.5px; color:#7a90b0; font-weight:500; }
  .meta-icon   { font-size:12px; }
  
  .read-more { display:inline-flex; align-items:center; gap:4px; font-size:13px; font-weight:700; color:#004AAD; text-decoration:none; transition:gap .15s; }
  .read-more:hover { gap:8px; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-outline-md { display:inline-flex; align-items:center; gap:8px; background:transparent; color:#004AAD; border:1.5px solid #004AAD; font-size:14px; font-weight:600; padding:9px 20px; border-radius:9px; text-decoration:none; transition:all .15s; }
  .btn-outline-md:hover { background:rgba(0,74,173,.06); }
  
  /* ── Pagination ──────────────────────────────────────────────────────── */
  .pagination-wrap { display:flex; justify-content:flex-end; margin-top:40px; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:900px)  { .posts-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:600px)  {
    .posts-grid    { grid-template-columns:1fr; }
    .section-pad   { padding:40px 0 60px; }
    .page-hero     { padding:64px 0 44px; }
    .list-header   { flex-direction:column; align-items:flex-start; }
    .inline-search--sm { width:100%; }
  }
  </style>